"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useMemo } from "react";
import { Drawer } from "@/components/ui";
import { getVisibleNavGroups } from "./nav-items";
import type { NavItem } from "./nav-items";
import { useCompareStore } from "@/features/compare/hooks/useCompareStore";
import { useRecentlyViewedStore } from "@/features/games/hooks/useRecentlyViewedStore";
import { useUser } from "@/features/auth/hooks/useUser";
import { hasApiAccess } from "@/lib/auth/access";
import { isAdminEmail, isSuperAdminEmail } from "@/lib/auth/admin";
import { isPaidPlan } from "@/lib/auth/plans";
import { GameImage } from "@/components/shared/game-image";
import { Clock } from "lucide-react";

type NavDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
};

function NavBadge({ item }: { item: NavItem }) {
  const { items: compareItems } = useCompareStore();

  if (item.badge === "compare" && compareItems.length > 0) {
    return (
      <span className="ml-auto flex-shrink-0 rounded-full bg-[#66c0f4]/15 px-2 py-0.5 text-[11px] font-semibold text-[#66c0f4]">
        {compareItems.length}
      </span>
    );
  }

  return null;
}

function RecentlyViewed({ onClose }: { onClose: () => void }) {
  const { items } = useRecentlyViewedStore();

  if (items.length === 0) return null;

  return (
    <div className="mt-3 border-t border-[#2a475e]/60 pt-3">
      <div className="px-3 pt-1 pb-1.5 flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#8f98a0]">
        <Clock className="w-3 h-3" />
        Recently Viewed
      </div>
      <div className="space-y-0.5">
        {items.map((game) => (
          <Link
            key={game.appid}
            href={`/app/${game.appid}`}
            onClick={onClose}
            className="group flex items-center gap-3 rounded-xl px-3 py-2 transition-colors text-[#c7d5e0] hover:bg-[#1f2e3f] hover:text-white"
          >
            <div className="flex-shrink-0 rounded overflow-hidden">
              <GameImage
                src={game.capsule_image}
                alt={game.name}
                width={40}
                height={15}
                className="object-cover"
              />
            </div>
            <span className="text-sm leading-5 truncate">
              {game.name}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function NavDrawer({ isOpen, onClose }: NavDrawerProps) {
  const pathname = usePathname();
  const { user, plan } = useUser();
  const isAdmin = isAdminEmail(user?.email);
  const isSuperAdmin = isSuperAdminEmail(user?.email);
  const isPaid = isPaidPlan(plan);
  const hasApi = hasApiAccess(plan, user?.email);
  const titleId = "nav-drawer-title";

  // Filter out gated items + entire groups that ended up empty (rule lives in
  // nav-items.ts). Memo so the filter doesn't run on every render; visibility
  // depends only on these four flags.
  const visibleGroups = useMemo(
    () => getVisibleNavGroups({ isAdmin, isSuperAdmin, isPaid, hasApi }),
    [isAdmin, isSuperAdmin, isPaid, hasApi],
  );

  const isActive = (href: string) => {
    if (href === "/") return pathname === "/";
    return pathname.startsWith(href);
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      position="left"
      width="86vw"
      maxWidth="380px"
      titleId={titleId}
    >
      <nav className="px-2 py-2">
        <span id={titleId} className="sr-only">
          Navigation
        </span>
        {visibleGroups.map((group, groupIdx) => (
          <div key={group.title} className={groupIdx > 0 ? "mt-3" : ""}>
            <div className="px-3 pt-2 pb-1.5 text-[11px] uppercase tracking-wider text-[#8f98a0]">
              {group.title}
            </div>
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href);
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={onClose}
                    className={[
                      "group flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors",
                      active
                        ? "bg-[#1f2e3f] text-[#66c0f4]"
                        : "text-[#c7d5e0] hover:bg-[#1f2e3f] hover:text-white",
                    ].join(" ")}
                  >
                    <Icon
                      className={[
                        "w-4 h-4 flex-shrink-0",
                        active
                          ? "text-[#66c0f4]"
                          : "text-[#8f98a0] group-hover:text-[#c7d5e0]",
                      ].join(" ")}
                    />
                    <span className="text-sm font-medium leading-5 truncate">
                      {item.label}
                    </span>
                    <NavBadge item={item} />
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
        <RecentlyViewed onClose={onClose} />
      </nav>
    </Drawer>
  );
}
